import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { QrCode } from '../components/Receipt.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi } from './Reports.tsx';

/**
 * Surveys: build a survey or a quiz from questions, open it, share its link or QR code, and watch
 * the answers come in: counts per choice, averages, the recommendation score and every written
 * answer.
 */

type Kind = 'section' | 'short' | 'long' | 'number' | 'date' | 'single' | 'multiple' | 'rating' | 'nps';
type Phase = 'draft' | 'open' | 'closed';
type Ask = 'no' | 'optional' | 'required';
type Tab = 'results' | 'responses' | 'build';

interface Option {
  id: string;
  label: string;
  correct?: boolean;
  points?: number;
}
interface Question {
  id: string;
  kind: Kind;
  title: string;
  help: string | null;
  required: boolean;
  options: Option[];
  min: number | null;
  max: number | null;
  show_if: { question: string; option: string } | null;
  answered?: boolean;
}
interface Row {
  id: string;
  ref: string;
  title: string;
  description: string | null;
  state: Phase;
  phase: Phase;
  quiz: boolean;
  token: string;
  closes: string | null;
  opened_at: string | null;
  responses: number;
  last_response: string | null;
  created_at: string;
}
interface List {
  surveys: Row[];
  counts: { open: number; responses: number; week: number };
  can: { manage: boolean };
}
interface Detail extends Row {
  thanks: string | null;
  pass_pct: number;
  ask_name: Ask;
  ask_email: Ask;
  show_answers: boolean;
  closed_at: string | null;
  questions: Question[];
  max_points: number;
  can: { manage: boolean };
}
type QResult = { id: string; kind: Kind; title: string; answered: number; skipped: number } & {
  options?: { id: string; label: string; correct: boolean; count: number }[];
  avg?: number | null;
  min?: number | null;
  max?: number | null;
  nps?: number | null;
  spread?: { value: number; count: number }[] | null;
  top?: number;
  texts?: { value: string; at: string }[];
};
interface Results {
  responses: number;
  median_seconds: number | null;
  quiz: boolean;
  quiz_stats: { avg_pct: number; passed: number } | null;
  by_day: { day: string; n: number }[];
  questions: QResult[];
}
interface ResponseRow {
  id: string;
  name: string | null;
  email: string | null;
  score: number;
  max_score: number;
  passed: boolean | null;
  seconds: number | null;
  created_at: string;
}

const KINDS: { kind: Kind; icon: IconName }[] = [
  { kind: 'single', icon: 'circle' },
  { kind: 'multiple', icon: 'check' },
  { kind: 'rating', icon: 'star' },
  { kind: 'nps', icon: 'chart' },
  { kind: 'short', icon: 'edit' },
  { kind: 'long', icon: 'note' },
  { kind: 'number', icon: 'percent' },
  { kind: 'date', icon: 'calendar' },
  { kind: 'section', icon: 'layers' },
];
const KIND_ICON = Object.fromEntries(KINDS.map((k) => [k.kind, k.icon])) as Record<Kind, IconName>;
const CHOICE: Kind[] = ['single', 'multiple'];
const PHASE_TONE: Record<Phase, Tone> = { draft: 'neutral', open: 'ok', closed: 'info' };
const PHASE_ICON: Record<Phase, IconName> = { draft: 'edit', open: 'circle', closed: 'lock' };
const surveyUrl = (tenant: string, token: string) => `${window.location.origin}/survey/${tenant}/${token}`;
const newId = () => Math.random().toString(36).slice(2, 10);
const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);
const duration = (s: number | null) => (s === null ? '—' : s < 60 ? t('sv.secs', { n: s }) : t('sv.mins', { m: Math.floor(s / 60), s: String(s % 60).padStart(2, '0') }));

function PhaseBadge({ phase }: { phase: Phase }) {
  return <StatusBadge tone={PHASE_TONE[phase]} icon={PHASE_ICON[phase]} label={t(`sv.ph_${phase}` as MessageKey)} pulse={phase === 'open'} />;
}

export function Surveys() {
  const [params, setParams] = useSearchParams();
  const id = params.get('id');
  const { data, reload } = useAction<List>('surveys.list');
  const [creating, setCreating] = useState(false);
  const open = (sid: string | null, tab?: Tab) => {
    const p = new URLSearchParams();
    if (sid) p.set('id', sid);
    if (sid && tab && tab !== 'results') p.set('tab', tab);
    setParams(p);
  };
  return (
    <Shell>
      <div className="page reports sv-page">
        {id ? (
          <SurveyView
            key={id}
            id={id}
            tab={(['responses', 'build'].includes(params.get('tab') ?? '') ? params.get('tab') : 'results') as Tab}
            setTab={(tab) => open(id, tab)}
            onBack={() => {
              open(null);
              void reload();
            }}
            onOpen={(sid, tab) => open(sid, tab)}
          />
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1>{t('sv.title')}</h1>
                <p className="muted">{t('sv.subtitle')}</p>
              </div>
              <div className="page-actions">
                {data?.can.manage ? (
                  <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                    {t('sv.new')}
                  </Button>
                ) : null}
              </div>
            </div>
            {!data ? (
              <Skeleton h={320} r="var(--radius-lg)" />
            ) : (
              <>
                <div className="rep-kpis sv-kpis">
                  <Kpi label={t('sv.k_open')} value={String(data.counts.open)} />
                  <Kpi label={t('sv.k_responses')} value={String(data.counts.responses)} />
                  <Kpi label={t('sv.k_week')} value={String(data.counts.week)} />
                </div>
                {!data.surveys.length ? (
                  <section className="panel">
                    <EmptyState
                      icon="survey"
                      title={t('sv.empty')}
                      body={t('sv.empty_body')}
                      action={
                        data.can.manage ? (
                          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                            {t('sv.new')}
                          </Button>
                        ) : undefined
                      }
                    />
                  </section>
                ) : (
                  <ul className="sv-grid" aria-label={t('sv.title')}>
                    {data.surveys.map((s) => (
                      <li key={s.id}>
                        <button type="button" className="sv-card" onClick={() => open(s.id)}>
                          <span className="sv-card-top">
                            <span className="sv-card-icon" aria-hidden="true">
                              <Icon name={s.quiz ? 'sparkle' : 'survey'} size={20} />
                            </span>
                            <PhaseBadge phase={s.phase} />
                          </span>
                          <span className="sv-card-title">{s.title}</span>
                          <span className="muted small">
                            {s.ref}
                            {s.quiz ? ` · ${t('sv.quiz')}` : ''}
                          </span>
                          <span className="sv-card-foot">
                            <span className="sv-card-n">{tn('sv.answers_n', s.responses)}</span>
                            <span className="muted small">{s.last_response ? t('sv.last', { when: dateTime(s.last_response) }) : s.phase === 'draft' ? t('sv.not_open') : t('sv.none_yet')}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </div>
      {creating ? (
        <NewSurvey
          onClose={() => setCreating(false)}
          onDone={(sid) => {
            setCreating(false);
            open(sid, 'build');
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- a new survey from a starting point ---------------------------------------------------

type Draft = Omit<Detail, 'id' | 'ref' | 'state' | 'phase' | 'token' | 'opened_at' | 'responses' | 'last_response' | 'created_at' | 'closed_at' | 'max_points' | 'can'>;

const opt = (label: string, correct?: boolean): Option => ({ id: newId(), label, ...(correct ? { correct: true, points: 1 } : {}) });
const q = (kind: Kind, title: string, extra: Partial<Question> = {}): Question => ({
  id: newId(),
  kind,
  title,
  help: null,
  required: kind !== 'section' && kind !== 'long',
  options: [],
  min: null,
  max: null,
  show_if: null,
  ...extra,
});
const blank = (title: string): Draft => ({ title, description: null, thanks: null, quiz: false, pass_pct: 70, ask_name: 'optional', ask_email: 'no', show_answers: true, closes: null, questions: [] });

const TEMPLATES: { key: string; icon: IconName; make: (title: string) => Draft }[] = [
  { key: 'blank', icon: 'file', make: (title) => ({ ...blank(title), questions: [q('single', '', { options: [opt(''), opt('')] })] }) },
  {
    key: 'satisfaction',
    icon: 'star',
    make: (title) => ({
      ...blank(title),
      thanks: t('sv.tpl_thanks'),
      questions: [
        q('rating', t('sv.tq_overall')),
        q('nps', t('sv.tq_recommend'), { help: t('sv.tq_recommend_help') }),
        q('long', t('sv.tq_liked'), { required: false }),
        q('long', t('sv.tq_better'), { required: false }),
      ],
    }),
  },
  {
    key: 'event',
    icon: 'ticket',
    make: (title) => ({
      ...blank(title),
      ask_email: 'optional',
      thanks: t('sv.tpl_thanks'),
      questions: [q('rating', t('sv.tq_event')), q('single', t('sv.tq_again'), { options: [opt(t('sv.yes')), opt(t('sv.maybe')), opt(t('sv.no'))] }), q('long', t('sv.tq_else'), { required: false })],
    }),
  },
  {
    key: 'quiz',
    icon: 'sparkle',
    make: (title) => ({
      ...blank(title),
      quiz: true,
      ask_name: 'required',
      questions: [q('single', '', { options: [opt('', true), opt('')] })],
    }),
  },
];

function NewSurvey({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [tpl, setTpl] = useState('satisfaction');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const submit = async () => {
    setTried(true);
    if (!title.trim()) return;
    const draft = TEMPLATES.find((x) => x.key === tpl)!.make(title.trim());
    // Empty texts from a blank start are filled in the builder; the server needs something now.
    const fill = draft.questions.map((x, i) => ({ ...x, title: x.title || t('sv.q_n', { n: i + 1 }), options: x.options.map((o, k) => ({ ...o, label: o.label || t('sv.choice_n', { n: k + 1 }) })) }));
    let id = '';
    if (await run(async () => (id = (await action<{ id: string }>('surveys.save', { ...draft, questions: fill })).id))) onDone(id);
  };
  return (
    <Dialog
      title={t('sv.new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void submit()}>
            {t('sv.create')}
          </Button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label={t('sv.f_title')} required error={tried && !title.trim() ? t('sv.need_title') : null}>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} autoFocus placeholder={t('sv.f_title_ph')} />
        </Field>
        <fieldset className="sv-tpls">
          <legend className="field-label">{t('sv.start_from')}</legend>
          {TEMPLATES.map((x) => (
            <label key={x.key} className={`sv-tpl${tpl === x.key ? ' on' : ''}`}>
              <input type="radio" name="tpl" value={x.key} checked={tpl === x.key} onChange={() => setTpl(x.key)} />
              <Icon name={x.icon} size={20} />
              <span>
                <strong>{t(`sv.tpl_${x.key}` as MessageKey)}</strong>
                <span className="muted small">{t(`sv.tpl_${x.key}_body` as MessageKey)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

// ---- one survey ---------------------------------------------------------------------------

function SurveyView({ id, tab, setTab, onBack, onOpen }: { id: string; tab: Tab; setTab: (t: Tab) => void; onBack: () => void; onOpen: (id: string, tab?: Tab) => void }) {
  const { data: s, reload } = useAction<Detail>('surveys.get', { id });
  const { tenant } = useSession();
  const [sharing, setSharing] = useState(false);
  const [ask, setAsk] = useState<'close' | 'remove' | 'draft' | null>(null);
  const [dirty, setDirty] = useState(false);
  const { busy, run } = useSave();
  const slug = tenant?.slug ?? '';
  const leave = (fn: () => void) => () => {
    if (dirty && !window.confirm(t('sv.leave_unsaved'))) return;
    setDirty(false);
    fn();
  };
  const state = async (to: Phase, done: string) => {
    if (await run(() => action('surveys.set_state', { id, state: to }), done)) {
      setAsk(null);
      await reload();
      if (to === 'open') setSharing(true);
    }
  };
  if (!s) return <Skeleton h={420} r="var(--radius-lg)" />;
  const questions = s.questions.filter((x) => x.kind !== 'section').length;
  return (
    <>
      <nav className="sv-crumbs" aria-label={t('sv.crumbs')}>
        <button type="button" className="link-button" onClick={leave(onBack)}>
          <Icon name="back" size={16} /> {t('sv.title')}
        </button>
      </nav>
      <div className="page-head sv-head">
        <div>
          <div className="sv-head-badges">
            <PhaseBadge phase={s.phase} />
            {s.quiz ? <StatusBadge tone="primary" icon="sparkle" label={t('sv.quiz')} /> : null}
            <span className="muted small">{s.ref}</span>
          </div>
          <h1>{s.title}</h1>
          <p className="muted">
            {tn('sv.questions_n', questions)} · {tn('sv.answers_n', s.responses)}
            {s.closes && s.phase === 'open' ? ` · ${t('sv.closes_on', { when: dateTime(s.closes) })}` : ''}
          </p>
        </div>
        <div className="page-actions">
          <Button icon="share" onClick={() => setSharing(true)}>
            {t('sv.share')}
          </Button>
          {s.can.manage && s.phase !== 'open' ? (
            <Button variant="primary" icon="send" busy={busy} disabled={dirty || !questions} onClick={() => void state('open', t('sv.opened'))} title={dirty ? t('sv.save_first') : undefined}>
              {s.phase === 'closed' ? t('sv.reopen') : t('sv.open')}
            </Button>
          ) : null}
          {s.can.manage && s.phase === 'open' ? (
            <Button icon="lock" onClick={() => setAsk('close')}>
              {t('sv.close')}
            </Button>
          ) : null}
          {s.can.manage ? (
            <MoreMenu
              s={s}
              onDuplicate={async () => {
                let nid = '';
                if (await run(async () => (nid = (await action<{ id: string }>('surveys.duplicate', { id })).id), t('sv.duplicated'))) onOpen(nid, 'build');
              }}
              onRemove={() => setAsk('remove')}
              onDraft={() => setAsk('draft')}
            />
          ) : null}
        </div>
      </div>
      <div className="segmented tabs" role="tablist" aria-label={t('sv.sections')}>
        {(['results', 'responses', 'build'] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={leave(() => setTab(k))}>
            <Icon name={k === 'results' ? 'chart' : k === 'responses' ? 'inbox' : 'edit'} size={16} />
            {t(`sv.tab_${k}` as MessageKey)}
            {k === 'responses' && s.responses ? <span className="count">{s.responses}</span> : null}
          </button>
        ))}
      </div>
      {tab === 'results' ? (
        <ResultsTab s={s} onBuild={() => setTab('build')} onShare={() => setSharing(true)} />
      ) : tab === 'responses' ? (
        <ResponsesTab s={s} onChange={reload} />
      ) : (
        <Builder
          s={s}
          onDirty={setDirty}
          onSaved={async () => {
            setDirty(false);
            await reload();
          }}
        />
      )}
      {sharing ? <ShareDialog s={s} url={surveyUrl(slug, s.token)} onClose={() => setSharing(false)} onOpenSurvey={() => void state('open', t('sv.opened'))} busy={busy} /> : null}
      {ask === 'close' ? (
        <Confirm title={t('sv.close_q')} body={t('sv.close_body')} confirm={t('sv.close')} onConfirm={() => void state('closed', t('sv.closed'))} onClose={() => setAsk(null)} busy={busy} />
      ) : null}
      {ask === 'draft' ? (
        <Confirm title={t('sv.draft_q')} body={t('sv.draft_body')} confirm={t('sv.to_draft')} onConfirm={() => void state('draft', t('sv.drafted'))} onClose={() => setAsk(null)} busy={busy} />
      ) : null}
      {ask === 'remove' ? (
        <Confirm
          danger
          title={t('sv.remove_q')}
          body={t('sv.remove_body')}
          confirm={t('sv.remove')}
          onConfirm={async () => {
            if (await run(() => action('surveys.remove', { id }), t('sv.removed'))) onBack();
          }}
          onClose={() => setAsk(null)}
          busy={busy}
        />
      ) : null}
    </>
  );
}

function MoreMenu({ s, onDuplicate, onRemove, onDraft }: { s: Detail; onDuplicate: () => void; onRemove: () => void; onDraft: () => void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('click', close);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', key);
    };
  }, [open]);
  const item = (icon: IconName, label: string, fn: () => void, danger = false) => (
    <button type="button" role="menuitem" className={`sv-menu-item${danger ? ' danger' : ''}`} onClick={fn}>
      <Icon name={icon} size={16} /> {label}
    </button>
  );
  return (
    <div className="sv-more" onClick={(e) => e.stopPropagation()}>
      <IconButton icon="dots" label={t('sv.more')} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} />
      {open ? (
        <div className="sv-menu" role="menu">
          {item('copy', t('sv.duplicate'), () => {
            setOpen(false);
            onDuplicate();
          })}
          {s.state !== 'draft' && !s.responses
            ? item('undo', t('sv.to_draft'), () => {
                setOpen(false);
                onDraft();
              })
            : null}
          {!s.responses
            ? item(
                'trash',
                t('sv.remove'),
                () => {
                  setOpen(false);
                  onRemove();
                },
                true,
              )
            : null}
        </div>
      ) : null}
    </div>
  );
}

// ---- sharing ------------------------------------------------------------------------------

function ShareDialog({ s, url, onClose, onOpenSurvey, busy }: { s: Detail; url: string; onClose: () => void; onOpenSurvey: () => void; busy: boolean }) {
  const { toast } = useUi();
  const { data } = useAction<{ rows: string[] }>('surveys.qr', { url });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast(t('sv.copied'));
    } catch {
      window.prompt(t('sv.copy_link'), url);
    }
  };
  const print = () => {
    const svg = document.querySelector('.sv-qr svg')?.outerHTML ?? '';
    const w = window.open('', '_blank', 'noopener=no,width=600,height=760');
    if (!w) return;
    const esc = (v: string) => v.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
    w.document.write(
      `<!doctype html><title>${esc(s.title)}</title><style>body{font:16px system-ui;text-align:center;padding:40px}svg{width:320px;height:320px}h1{font-size:26px}p{opacity:.75}</style><h1>${esc(s.title)}</h1><p>${esc(t('sv.scan'))}</p>${svg}<p>${esc(url)}</p><script>onload=()=>print()</script>`,
    );
    w.document.close();
  };
  return (
    <Dialog
      title={t('sv.share_title')}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {s.phase !== 'open' ? (
        <div className="sv-note warn" role="status">
          <Icon name="alert" size={18} />
          <span>{s.phase === 'draft' ? t('sv.share_draft') : t('sv.share_closed')}</span>
          {s.can.manage ? (
            <Button size="sm" variant="primary" busy={busy} onClick={onOpenSurvey}>
              {s.phase === 'closed' ? t('sv.reopen') : t('sv.open')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="sv-share">
        <div className="sv-qr">{data ? <QrCode rows={data.rows} label={t('sv.qr_label', { title: s.title })} /> : <Skeleton h={200} w={200} />}</div>
        <div className="sv-share-side">
          <Field label={t('sv.link')}>
            <input className="input" readOnly value={url} onFocus={(e) => e.target.select()} />
          </Field>
          <div className="sv-share-btns">
            <Button icon="copy" onClick={() => void copy()}>
              {t('sv.copy_link')}
            </Button>
            <a className="btn btn-ghost btn-md" href={url} target="_blank" rel="noopener noreferrer">
              <Icon name="external" size={20} />
              <span className="btn-label">{s.phase === 'open' ? t('sv.open_page') : t('sv.preview')}</span>
            </a>
            <Button variant="ghost" icon="printer" onClick={print} disabled={!data}>
              {t('sv.print_qr')}
            </Button>
          </div>
          <p className="muted small">{t('sv.share_hint')}</p>
        </div>
      </div>
    </Dialog>
  );
}

// ---- results ------------------------------------------------------------------------------

function ResultsTab({ s, onBuild, onShare }: { s: Detail; onBuild: () => void; onShare: () => void }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data: r } = useAction<Results>('surveys.results', { id: s.id, from: from || null, to: to || null });
  const exportCsv = async () => {
    const x = await action<{ head: string[]; rows: { at: string; name: string | null; email: string | null; score: number; max_score: number; answers: string[] }[] }>('surveys.export', { id: s.id });
    downloadCsv(
      `${s.ref}.csv`,
      [t('sv.c_when'), t('sv.c_name'), t('sv.c_email'), ...(s.quiz ? [t('sv.c_score')] : []), ...x.head],
      x.rows.map((row) => [dateTime(row.at), row.name ?? '', row.email ?? '', ...(s.quiz ? [`${row.score}/${row.max_score}`] : []), ...row.answers]),
    );
  };
  if (!s.responses)
    return (
      <section className="panel">
        <EmptyState
          icon="chart"
          title={t('sv.no_results')}
          body={s.phase === 'draft' ? t('sv.no_results_draft') : t('sv.no_results_open')}
          action={
            s.phase === 'draft' && s.can.manage ? (
              <Button icon="edit" onClick={onBuild}>
                {t('sv.edit_questions')}
              </Button>
            ) : (
              <Button icon="share" onClick={onShare}>
                {t('sv.share')}
              </Button>
            )
          }
        />
      </section>
    );
  const peak = Math.max(1, ...(r?.by_day.map((d) => d.n) ?? [1]));
  return (
    <div className="sv-results">
      <div className="ev-filters sv-filters">
        <div className="sv-range">
          <label>
            <span className="small muted">{t('sv.from')}</span>
            <input className="input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            <span className="small muted">{t('sv.to')}</span>
            <input className="input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </label>
          {from || to ? (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
            >
              {t('sv.all_time')}
            </button>
          ) : null}
        </div>
        <Button icon="download" onClick={() => void exportCsv()}>
          {t('sv.export')}
        </Button>
      </div>
      {!r ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : (
        <>
          <div className="rep-kpis sv-kpis">
            <Kpi label={t('sv.k_responses')} value={String(r.responses)} />
            <Kpi label={t('sv.k_time')} value={duration(r.median_seconds)} hint={t('sv.k_time_hint')} />
            {r.quiz_stats ? (
              <>
                <Kpi label={t('sv.k_avg_score')} value={`${r.quiz_stats.avg_pct} %`} />
                <Kpi label={t('sv.k_passed')} value={`${r.quiz_stats.passed} / ${r.responses}`} hint={t('sv.pass_at', { n: s.pass_pct })} />
              </>
            ) : null}
          </div>
          {r.by_day.length > 1 ? (
            <section className="panel sv-days">
              <h2 className="panel-title">{t('sv.by_day')}</h2>
              <div className="sv-bars" role="img" aria-label={t('sv.by_day_label', { n: r.responses, days: r.by_day.length })}>
                {r.by_day.map((d) => (
                  <span key={d.day} className="sv-bar" style={{ height: `${Math.max(6, (d.n / peak) * 100)}%` }} title={`${dayText(d.day)}: ${d.n}`} />
                ))}
              </div>
              <div className="sv-bars-axis muted small">
                <span>{dayText(r.by_day[0]!.day)}</span>
                <span>{dayText(r.by_day[r.by_day.length - 1]!.day)}</span>
              </div>
            </section>
          ) : null}
          {!r.responses ? (
            <section className="panel">
              <EmptyState icon="search" title={t('sv.none_in_range')} />
            </section>
          ) : (
            r.questions.map((x, i) => <QuestionResult key={x.id} n={i + 1} x={x} total={r.responses} quiz={s.quiz} />)
          )}
        </>
      )}
    </div>
  );
}

function QuestionResult({ n, x, total, quiz }: { n: number; x: QResult; total: number; quiz: boolean }) {
  const [all, setAll] = useState(false);
  let body: ReactNode = null;
  if (x.options) {
    const base = x.answered;
    body = (
      <ul className="sv-opts">
        {x.options.map((o) => (
          <li key={o.id} className={quiz && o.correct ? 'right' : ''}>
            <span className="sv-opt-label">
              {quiz && o.correct ? <Icon name="check" size={14} /> : null}
              {o.label}
            </span>
            <span className="sv-meter" aria-hidden="true">
              <span style={{ width: `${pct(o.count, base)}%` }} />
            </span>
            <span className="sv-opt-n">
              <strong>{pct(o.count, base)} %</strong> <span className="muted small">({o.count})</span>
            </span>
          </li>
        ))}
      </ul>
    );
  } else if (x.kind === 'nps' && x.spread) {
    const det = x.spread.filter((v) => v.value <= 6).reduce((a, v) => a + v.count, 0);
    const pas = x.spread.filter((v) => v.value === 7 || v.value === 8).reduce((a, v) => a + v.count, 0);
    const pro = x.spread.filter((v) => v.value >= 9).reduce((a, v) => a + v.count, 0);
    body = (
      <div className="sv-nps">
        <div className="sv-nps-score">
          <span className={`sv-big ${(x.nps ?? 0) >= 30 ? 'good' : (x.nps ?? 0) < 0 ? 'bad' : ''}`}>{x.nps === null || x.nps === undefined ? '—' : x.nps > 0 ? `+${x.nps}` : x.nps}</span>
          <span className="muted small">{t('sv.nps_hint')}</span>
        </div>
        <div className="sv-nps-split">
          <div className="sv-stack" aria-hidden="true">
            <span className="det" style={{ width: `${pct(det, x.answered)}%` }} />
            <span className="pas" style={{ width: `${pct(pas, x.answered)}%` }} />
            <span className="pro" style={{ width: `${pct(pro, x.answered)}%` }} />
          </div>
          <ul className="sv-legend">
            <li>
              <span className="dot det" /> {t('sv.detractors')} <strong>{pct(det, x.answered)} %</strong>
            </li>
            <li>
              <span className="dot pas" /> {t('sv.passives')} <strong>{pct(pas, x.answered)} %</strong>
            </li>
            <li>
              <span className="dot pro" /> {t('sv.promoters')} <strong>{pct(pro, x.answered)} %</strong>
            </li>
          </ul>
          <Spread spread={x.spread} />
        </div>
      </div>
    );
  } else if (x.kind === 'rating' && x.spread) {
    body = (
      <div className="sv-rating">
        <div className="sv-nps-score">
          <span className="sv-big">{x.avg ?? '—'}</span>
          <span className="sv-stars" aria-label={t('sv.avg_of', { avg: x.avg ?? 0, top: 5 })}>
            {[1, 2, 3, 4, 5].map((k) => (
              <Icon key={k} name="star" size={16} className={(x.avg ?? 0) >= k - 0.25 ? 'on' : ''} />
            ))}
          </span>
        </div>
        <Spread spread={x.spread} />
      </div>
    );
  } else if (x.kind === 'number') {
    body = (
      <dl className="sv-stats">
        <div>
          <dt>{t('sv.avg')}</dt>
          <dd>{x.avg ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('sv.lowest')}</dt>
          <dd>{x.min ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('sv.highest')}</dt>
          <dd>{x.max ?? '—'}</dd>
        </div>
      </dl>
    );
  } else if (x.texts) {
    const shown = all ? x.texts : x.texts.slice(0, 5);
    body = x.texts.length ? (
      <>
        <ul className="sv-texts">
          {shown.map((v, k) => (
            <li key={k}>
              <span>{x.kind === 'date' ? dayText(v.value) : v.value}</span>
              <span className="muted small">{dateTime(v.at)}</span>
            </li>
          ))}
        </ul>
        {x.texts.length > 5 ? (
          <button type="button" className="link-button" onClick={() => setAll((a) => !a)}>
            {all ? t('sv.show_less') : t('sv.show_all', { n: x.texts.length })}
          </button>
        ) : null}
      </>
    ) : (
      <p className="muted">{t('sv.no_text')}</p>
    );
  }
  return (
    <section className="panel sv-qres">
      <header className="sv-qres-head">
        <span className="sv-qnum" aria-hidden="true">
          {n}
        </span>
        <div>
          <h3>{x.title}</h3>
          <p className="muted small">
            <Icon name={KIND_ICON[x.kind]} size={14} /> {t(`sv.k_${x.kind}` as MessageKey)} · {t('sv.answered_of', { n: x.answered, of: total })}
          </p>
        </div>
      </header>
      {body}
    </section>
  );
}

function Spread({ spread }: { spread: { value: number; count: number }[] }) {
  const peak = Math.max(1, ...spread.map((v) => v.count));
  return (
    <div className="sv-spread" role="img" aria-label={spread.map((v) => `${v.value}: ${v.count}`).join(', ')}>
      {spread.map((v) => (
        <span key={v.value} className="sv-spread-col">
          <span className="sv-spread-n small muted">{v.count || ''}</span>
          <span className="sv-spread-bar" style={{ height: `${(v.count / peak) * 100}%` }} />
          <span className="sv-spread-v small">{v.value}</span>
        </span>
      ))}
    </div>
  );
}

// ---- responses ----------------------------------------------------------------------------

function ResponsesTab({ s, onChange }: { s: Detail; onChange: () => void }) {
  const { data: rows, reload } = useAction<ResponseRow[]>('surveys.responses', { id: s.id });
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const k = q.trim().toLowerCase();
    return (rows ?? []).filter((r) => !k || `${r.name ?? ''} ${r.email ?? ''}`.toLowerCase().includes(k));
  }, [rows, q]);
  if (!rows) return <Skeleton h={320} r="var(--radius-lg)" />;
  if (!rows.length)
    return (
      <section className="panel">
        <EmptyState icon="inbox" title={t('sv.no_responses')} body={t('sv.no_responses_body')} />
      </section>
    );
  const index = (id: string) => list.findIndex((r) => r.id === id);
  return (
    <section className="panel">
      <div className="ev-filters">
        <span className="muted">{tn('sv.answers_n', rows.length)}</span>
        <div className="ev-filter-end">
          <input className="input ev-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('sv.search')} aria-label={t('sv.search')} />
        </div>
      </div>
      {!list.length ? (
        <EmptyState icon="search" title={t('sv.no_match')} />
      ) : (
        <TableWrap>
          <table className="data-table sv-table">
            <thead>
              <tr>
                <th className="left">{t('sv.c_when')}</th>
                <th className="left">{t('sv.c_who')}</th>
                {s.quiz ? <th>{t('sv.c_score')}</th> : null}
                <th>{t('sv.c_time')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="clickable" onClick={() => setOpen(r.id)}>
                  <td className="left">
                    <button type="button" className="row-link" onClick={() => setOpen(r.id)}>
                      {dateTime(r.created_at)}
                    </button>
                  </td>
                  <td className="left">
                    {r.name || r.email ? (
                      <>
                        {r.name ?? ''}
                        {r.email ? <span className="muted small"> {r.email}</span> : null}
                      </>
                    ) : (
                      <span className="muted">{t('sv.anonymous')}</span>
                    )}
                  </td>
                  {s.quiz ? (
                    <td>
                      <StatusBadge tone={r.passed ? 'ok' : 'warn'} icon={r.passed ? 'check' : 'x'} label={`${pct(r.score, r.max_score)} %`} />
                    </td>
                  ) : null}
                  <td>{duration(r.seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {open ? (
        <ResponseDialog
          key={open}
          id={open}
          can={s.can.manage}
          prev={index(open) > 0 ? () => setOpen(list[index(open) - 1]!.id) : null}
          next={index(open) < list.length - 1 ? () => setOpen(list[index(open) + 1]!.id) : null}
          onClose={() => setOpen(null)}
          onRemoved={() => {
            setOpen(null);
            void reload();
            onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function ResponseDialog({ id, can, prev, next, onClose, onRemoved }: { id: string; can: boolean; prev: (() => void) | null; next: (() => void) | null; onClose: () => void; onRemoved: () => void }) {
  const { data: r } = useAction<ResponseRow & { items: { id: string; kind: Kind; title: string; options: Option[]; value: unknown }[] }>('surveys.response', { id });
  const [ask, setAsk] = useState(false);
  const { busy, run } = useSave();
  const show = (x: { kind: Kind; options: Option[]; value: unknown }) => {
    if (x.value === null || x.value === undefined) return <span className="muted">{t('sv.skipped')}</span>;
    if (CHOICE.includes(x.kind))
      return (
        <ul className="sv-chosen">
          {(x.value as string[]).map((v) => {
            const o = x.options.find((k) => k.id === v);
            return (
              <li key={v} className={o?.correct ? 'right' : ''}>
                {o?.label ?? '—'}
              </li>
            );
          })}
        </ul>
      );
    if (x.kind === 'rating') return <span>{t('sv.out_of', { n: Number(x.value), top: 5 })}</span>;
    if (x.kind === 'nps') return <span>{t('sv.out_of', { n: Number(x.value), top: 10 })}</span>;
    if (x.kind === 'date') return <span>{dayText(String(x.value))}</span>;
    return <span className="sv-answer-text">{String(x.value)}</span>;
  };
  return (
    <Dialog
      title={r ? r.name || t('sv.anonymous') : t('sv.response')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {can ? (
            <Button variant="ghost" icon="trash" onClick={() => setAsk(true)} className="sv-left">
              {t('sv.remove_response')}
            </Button>
          ) : null}
          <IconButton icon="back" label={t('sv.previous')} disabled={!prev} onClick={() => prev?.()} />
          <IconButton icon="back" className="flip" label={t('sv.next')} disabled={!next} onClick={() => next?.()} />
        </>
      }
    >
      {!r ? (
        <Skeleton h={240} />
      ) : (
        <>
          <p className="muted small sv-resp-meta">
            {dateTime(r.created_at)}
            {r.email ? ` · ${r.email}` : ''}
            {r.seconds !== null ? ` · ${duration(r.seconds)}` : ''}
            {r.max_score ? ` · ${t('sv.score_of', { n: r.score, of: r.max_score })}` : ''}
          </p>
          <dl className="sv-resp">
            {r.items.map((x) =>
              x.kind === 'section' ? (
                <h3 key={x.id} className="sv-resp-section">
                  {x.title}
                </h3>
              ) : (
                <div key={x.id}>
                  <dt>{x.title}</dt>
                  <dd>{show(x)}</dd>
                </div>
              ),
            )}
          </dl>
        </>
      )}
      {ask ? (
        <Confirm
          danger
          title={t('sv.remove_response_q')}
          body={t('sv.remove_response_body')}
          confirm={t('sv.remove')}
          busy={busy}
          onConfirm={async () => {
            if (await run(() => action('surveys.remove_response', { id }), t('sv.response_removed'))) onRemoved();
          }}
          onClose={() => setAsk(false)}
        />
      ) : null}
    </Dialog>
  );
}

// ---- building ------------------------------------------------------------------------------

const toLocal = (v: string | null) => {
  if (!v) return '';
  const d = new Date(v);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function Builder({ s, onDirty, onSaved }: { s: Detail; onDirty: (v: boolean) => void; onSaved: () => Promise<void> }) {
  const initial = useMemo<Draft>(
    () => ({
      title: s.title,
      description: s.description,
      thanks: s.thanks,
      quiz: s.quiz,
      pass_pct: s.pass_pct,
      ask_name: s.ask_name,
      ask_email: s.ask_email,
      show_answers: s.show_answers,
      closes: s.closes,
      questions: s.questions.map((x) => ({ ...x, options: x.options.map((o) => ({ ...o })) })),
    }),
    [s],
  );
  const [d, setD] = useState<Draft>(initial);
  const [tried, setTried] = useState(false);
  const [adding, setAdding] = useState<number | null>(null);
  const { busy, run } = useSave();
  const dirty = JSON.stringify(d) !== JSON.stringify(initial);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const readOnly = !s.can.manage;
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const setQ = (i: number, patch: Partial<Question>) => setD((x) => ({ ...x, questions: x.questions.map((y, k) => (k === i ? { ...y, ...patch } : y)) }));
  const move = (i: number, by: number) =>
    setD((x) => {
      const qs = [...x.questions];
      const [it] = qs.splice(i, 1);
      qs.splice(i + by, 0, it!);
      // A condition must point back: drop the ones that now point forward.
      const seen = new Set<string>();
      return { ...x, questions: qs.map((y) => (seen.add(y.id), y.show_if && !seen.has(y.show_if.question) ? { ...y, show_if: null } : y)) };
    });
  const remove = (i: number) =>
    setD((x) => {
      const gone = x.questions[i]!.id;
      return { ...x, questions: x.questions.filter((_, k) => k !== i).map((y) => (y.show_if?.question === gone ? { ...y, show_if: null } : y)) };
    });
  const copyQ = (i: number) =>
    setD((x) => {
      const src = x.questions[i]!;
      const qs = [...x.questions];
      qs.splice(i + 1, 0, { ...src, id: newId(), answered: false, options: src.options.map((o) => ({ ...o, id: newId() })) });
      return { ...x, questions: qs };
    });
  const add = (kind: Kind, at: number) => {
    const made = q(kind, '', CHOICE.includes(kind) ? { options: [opt(''), opt('')] } : {});
    setD((x) => {
      const qs = [...x.questions];
      qs.splice(at, 0, made);
      return { ...x, questions: qs };
    });
    setAdding(null);
    window.setTimeout(() => document.getElementById(`sv-q-${made.id}`)?.focus(), 30);
  };
  // What must be fixed before saving, per question.
  const problems = useMemo(() => {
    const out = new Map<string, string>();
    for (const x of d.questions) {
      if (!x.title.trim()) out.set(x.id, t('sv.need_text'));
      else if (CHOICE.includes(x.kind) && x.options.length < 2) out.set(x.id, t('sv.need_two'));
      else if (CHOICE.includes(x.kind) && x.options.some((o) => !o.label.trim())) out.set(x.id, t('sv.need_choice_text'));
      else if (x.kind === 'number' && x.min !== null && x.max !== null && x.min > x.max) out.set(x.id, t('sv.need_range'));
      else if (d.quiz && CHOICE.includes(x.kind) && !x.options.some((o) => o.correct)) out.set(x.id, t('sv.need_right'));
    }
    return out;
  }, [d]);
  const save = async () => {
    setTried(true);
    if (!d.title.trim() || problems.size) {
      document.querySelector('.sv-builder .is-invalid, .sv-builder [aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const body = {
      id: s.id,
      ...d,
      questions: d.questions.map((x) => ({
        id: x.id,
        kind: x.kind,
        title: x.title,
        help: x.help,
        required: x.required,
        options: x.options.map((o) => ({ id: o.id, label: o.label, ...(d.quiz ? { correct: !!o.correct, points: o.points ?? (o.correct ? 1 : 0) } : {}) })),
        min: x.min,
        max: x.max,
        show_if: x.show_if,
      })),
    };
    if (await run(() => action('surveys.save', body), t('sv.saved'))) await onSaved();
  };
  const removedAnswered = s.questions.filter((x) => x.answered && !d.questions.some((y) => y.id === x.id)).length;
  return (
    <div className="sv-builder">
      <fieldset className="panel sv-settings" disabled={readOnly}>
        <legend className="sr-only">{t('sv.settings')}</legend>
        <div className="sv-settings-grid">
          <Field label={t('sv.f_title')} required error={tried && !d.title.trim() ? t('sv.need_title') : null}>
            <input className="input" value={d.title} maxLength={160} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label={t('sv.f_closes')} hint={t('sv.f_closes_hint')}>
            <input className="input" type="datetime-local" value={toLocal(d.closes)} onChange={(e) => set({ closes: e.target.value ? new Date(e.target.value).toISOString() : null })} />
          </Field>
          <Field label={t('sv.f_intro')} hint={t('sv.f_intro_hint')}>
            <textarea className="input textarea" rows={2} value={d.description ?? ''} maxLength={2000} onChange={(e) => set({ description: e.target.value || null })} />
          </Field>
          <Field label={t('sv.f_thanks')} hint={t('sv.f_thanks_hint')}>
            <textarea className="input textarea" rows={2} value={d.thanks ?? ''} maxLength={1000} onChange={(e) => set({ thanks: e.target.value || null })} />
          </Field>
          <Field label={t('sv.f_ask_name')}>
            <select className="input" value={d.ask_name} onChange={(e) => set({ ask_name: e.target.value as Ask })}>
              {(['no', 'optional', 'required'] as const).map((a) => (
                <option key={a} value={a}>
                  {t(`sv.ask_${a}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('sv.f_ask_email')}>
            <select className="input" value={d.ask_email} onChange={(e) => set({ ask_email: e.target.value as Ask })}>
              {(['no', 'optional', 'required'] as const).map((a) => (
                <option key={a} value={a}>
                  {t(`sv.ask_${a}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="sv-quiz-row">
          <LabeledSwitch checked={d.quiz} onChange={(v) => set({ quiz: v })} label={t('sv.f_quiz')} disabled={readOnly} />
          <span className="muted small">{t('sv.f_quiz_hint')}</span>
        </div>
        {d.quiz ? (
          <div className="sv-settings-grid sv-quiz-opts">
            <Field label={t('sv.f_pass')} hint={t('sv.f_pass_hint')}>
              <input
                className="input sv-num"
                type="number"
                min={0}
                max={100}
                value={d.pass_pct}
                onChange={(e) => set({ pass_pct: Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))) })}
              />
            </Field>
            <div className="sv-switch-field">
              <LabeledSwitch checked={d.show_answers} onChange={(v) => set({ show_answers: v })} label={t('sv.f_show_answers')} disabled={readOnly} />
            </div>
          </div>
        ) : null}
      </fieldset>

      <ol className="sv-qs">
        {d.questions.map((x, i) => (
          <li key={x.id}>
            {!readOnly ? <AddBar open={adding === i} onToggle={() => setAdding(adding === i ? null : i)} onAdd={(k) => add(k, i)} /> : null}
            <QuestionEditor
              x={x}
              n={d.questions.slice(0, i + 1).filter((y) => y.kind !== 'section').length}
              earlier={d.questions.slice(0, i).filter((y) => CHOICE.includes(y.kind))}
              quiz={d.quiz}
              readOnly={readOnly}
              error={tried ? (problems.get(x.id) ?? null) : null}
              first={i === 0}
              last={i === d.questions.length - 1}
              onChange={(patch) => setQ(i, patch)}
              onMove={(by) => move(i, by)}
              onCopy={() => copyQ(i)}
              onRemove={() => remove(i)}
            />
          </li>
        ))}
      </ol>
      {!readOnly ? (
        <div className="sv-add-end">
          <p className="muted small">{d.questions.length ? t('sv.add_more') : t('sv.add_first')}</p>
          <div className="sv-kinds">
            {KINDS.map((k) => (
              <button key={k.kind} type="button" className="sv-kind" onClick={() => add(k.kind, d.questions.length)}>
                <Icon name={k.icon} size={18} />
                {t(`sv.k_${k.kind}` as MessageKey)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {!readOnly && (dirty || tried) ? (
        <div className="sv-savebar" role="region" aria-label={t('sv.unsaved')}>
          <span>
            {removedAnswered ? (
              <span className="sv-warn-text">
                <Icon name="alert" size={16} /> {tn('sv.removes_answers', removedAnswered)}
              </span>
            ) : tried && problems.size ? (
              <span className="sv-warn-text">
                <Icon name="alert" size={16} /> {tn('sv.fix_n', problems.size)}
              </span>
            ) : (
              t('sv.unsaved')
            )}
          </span>
          <span className="sv-savebar-btns">
            <Button
              variant="ghost"
              disabled={!dirty}
              onClick={() => {
                setD(initial);
                setTried(false);
              }}
            >
              {t('sv.discard')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={!dirty} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
}

function AddBar({ open, onToggle, onAdd }: { open: boolean; onToggle: () => void; onAdd: (k: Kind) => void }) {
  return (
    <div className={`sv-addbar${open ? ' open' : ''}`}>
      <button type="button" className="sv-addbar-btn" onClick={onToggle} aria-expanded={open} aria-label={t('sv.insert_here')} title={t('sv.insert_here')}>
        <Icon name={open ? 'x' : 'plus'} size={14} />
      </button>
      {open ? (
        <div className="sv-kinds">
          {KINDS.map((k) => (
            <button key={k.kind} type="button" className="sv-kind" onClick={() => onAdd(k.kind)}>
              <Icon name={k.icon} size={16} />
              {t(`sv.k_${k.kind}` as MessageKey)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function QuestionEditor({
  x,
  n,
  earlier,
  quiz,
  readOnly,
  error,
  first,
  last,
  onChange,
  onMove,
  onCopy,
  onRemove,
}: {
  x: Question;
  n: number;
  earlier: Question[];
  quiz: boolean;
  readOnly: boolean;
  error: string | null;
  first: boolean;
  last: boolean;
  onChange: (patch: Partial<Question>) => void;
  onMove: (by: number) => void;
  onCopy: () => void;
  onRemove: () => void;
}) {
  const section = x.kind === 'section';
  const choice = CHOICE.includes(x.kind);
  const setOpt = (k: number, patch: Partial<Option>) =>
    onChange({ options: x.options.map((o, i) => (i === k ? { ...o, ...patch } : quiz && x.kind === 'single' && patch.correct ? { ...o, correct: false } : o)) });
  const changeKind = (kind: Kind) => {
    const toChoice = CHOICE.includes(kind);
    onChange({
      kind,
      options: toChoice ? (x.options.length ? x.options : [opt(''), opt('')]) : [],
      required: kind === 'section' ? false : x.required,
      ...(kind === 'single' && x.options.filter((o) => o.correct).length > 1 ? { options: x.options.map((o, i) => ({ ...o, correct: i === x.options.findIndex((p) => p.correct) })) } : {}),
    });
  };
  const cond = x.show_if ? earlier.find((y) => y.id === x.show_if!.question) : null;
  return (
    <fieldset
      className={`panel sv-q${section ? ' is-section' : ''}${error ? ' is-invalid' : ''}`}
      disabled={readOnly}
      aria-label={section ? t('sv.section_label', { title: x.title }) : t('sv.question_label', { n })}
    >
      <div className="sv-q-head">
        <span className="sv-qnum" aria-hidden="true">
          {section ? <Icon name="layers" size={14} /> : n}
        </span>
        <select className="input sv-q-kind" value={x.kind} onChange={(e) => changeKind(e.target.value as Kind)} aria-label={t('sv.q_kind')}>
          {KINDS.map((k) => (
            <option key={k.kind} value={k.kind}>
              {t(`sv.k_${k.kind}` as MessageKey)}
            </option>
          ))}
        </select>
        {x.answered ? (
          <span className="sv-chip" title={t('sv.has_answers_hint')}>
            <Icon name="inbox" size={12} /> {t('sv.has_answers')}
          </span>
        ) : null}
        {!readOnly ? (
          <span className="sv-q-tools">
            <IconButton icon="up" label={t('sv.move_up')} disabled={first} onClick={() => onMove(-1)} />
            <IconButton icon="down" label={t('sv.move_down')} disabled={last} onClick={() => onMove(1)} />
            <IconButton icon="copy" label={t('sv.copy_q')} onClick={onCopy} />
            <IconButton icon="trash" label={t('sv.remove_q_btn')} onClick={onRemove} />
          </span>
        ) : null}
      </div>
      <input
        id={`sv-q-${x.id}`}
        className={`input sv-q-title${section ? ' big' : ''}`}
        value={x.title}
        maxLength={300}
        placeholder={section ? t('sv.section_ph') : t('sv.question_ph')}
        aria-label={section ? t('sv.section_title') : t('sv.question_text')}
        aria-invalid={!!error && !x.title.trim()}
        onChange={(e) => onChange({ title: e.target.value })}
      />
      <input
        className="input sv-q-help"
        value={x.help ?? ''}
        maxLength={600}
        placeholder={section ? t('sv.section_help_ph') : t('sv.help_ph')}
        aria-label={t('sv.help_label')}
        onChange={(e) => onChange({ help: e.target.value || null })}
      />
      {choice ? (
        <ul className="sv-q-opts">
          {x.options.map((o, k) => (
            <li key={o.id}>
              {quiz ? (
                <label className={`sv-right${o.correct ? ' on' : ''}`} title={t('sv.right_answer')}>
                  <input type={x.kind === 'single' ? 'radio' : 'checkbox'} name={`right-${x.id}`} checked={!!o.correct} onChange={(e) => setOpt(k, { correct: e.target.checked })} />
                  <span className="sr-only">{t('sv.right_answer')}</span>
                </label>
              ) : (
                <span className={`sv-opt-mark ${x.kind}`} aria-hidden="true" />
              )}
              <input
                className="input"
                value={o.label}
                maxLength={200}
                placeholder={t('sv.choice_n', { n: k + 1 })}
                aria-label={t('sv.choice_n', { n: k + 1 })}
                aria-invalid={!!error && !o.label.trim()}
                onChange={(e) => setOpt(k, { label: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const made = opt('');
                    onChange({ options: [...x.options.slice(0, k + 1), made, ...x.options.slice(k + 1)] });
                    window.setTimeout(() => (document.querySelector(`[data-opt="${made.id}"]`) as HTMLInputElement | null)?.focus(), 20);
                  }
                }}
                data-opt={o.id}
              />
              {quiz ? (
                <input
                  className="input sv-points"
                  type="number"
                  min={0}
                  max={100}
                  value={o.points ?? (o.correct ? 1 : 0)}
                  aria-label={t('sv.points')}
                  title={t('sv.points')}
                  onChange={(e) => setOpt(k, { points: Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))) })}
                />
              ) : null}
              {!readOnly ? <IconButton icon="x" label={t('sv.remove_choice')} disabled={x.options.length <= 2} onClick={() => onChange({ options: x.options.filter((_, i) => i !== k) })} /> : null}
            </li>
          ))}
          {!readOnly && x.options.length < 40 ? (
            <li>
              <button type="button" className="link-button sv-add-opt" onClick={() => onChange({ options: [...x.options, opt('')] })}>
                <Icon name="plus" size={14} /> {t('sv.add_choice')}
              </button>
            </li>
          ) : null}
        </ul>
      ) : x.kind === 'number' ? (
        <div className="sv-range sv-q-range">
          <label>
            <span className="small muted">{t('sv.lowest')}</span>
            <input className="input sv-num" type="number" value={x.min ?? ''} onChange={(e) => onChange({ min: e.target.value === '' ? null : Math.round(Number(e.target.value)) })} />
          </label>
          <label>
            <span className="small muted">{t('sv.highest')}</span>
            <input className="input sv-num" type="number" value={x.max ?? ''} onChange={(e) => onChange({ max: e.target.value === '' ? null : Math.round(Number(e.target.value)) })} />
          </label>
        </div>
      ) : !section ? (
        <Preview kind={x.kind} />
      ) : null}
      {error ? (
        <p className="ap-error" role="alert">
          <Icon name="alert" size={14} /> {error}
        </p>
      ) : null}
      {!section ? (
        <div className="sv-q-foot">
          <LabeledSwitch checked={x.required} onChange={(v) => onChange({ required: v })} label={t('sv.required')} disabled={readOnly} />
          {earlier.length ? (
            <div className="sv-cond">
              <Icon name="branch" size={14} />
              <select
                className="input"
                aria-label={t('sv.show_if')}
                value={x.show_if ? `${x.show_if.question}|${x.show_if.option}` : ''}
                onChange={(e) => {
                  const [question, option] = e.target.value.split('|');
                  onChange({ show_if: question && option ? { question, option } : null });
                }}
              >
                <option value="">{t('sv.always_show')}</option>
                {earlier.map((y) => (
                  <optgroup key={y.id} label={y.title || t('sv.untitled')}>
                    {y.options.map((o) => (
                      <option key={o.id} value={`${y.id}|${o.id}`}>
                        {t('sv.show_when', { answer: o.label || '…' })}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      ) : null}
      {cond ? (
        <p className="muted small sv-cond-note">
          <Icon name="branch" size={12} /> {t('sv.shown_only', { q: cond.title, a: cond.options.find((o) => o.id === x.show_if!.option)?.label ?? '' })}
        </p>
      ) : null}
    </fieldset>
  );
}

/** A switch with its name written beside it; the text toggles it too. */
function LabeledSwitch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <span className="sv-switch">
      <Switch checked={checked} onChange={onChange} label={label} disabled={disabled} />
      <span aria-hidden="true" onClick={() => !disabled && onChange(!checked)}>
        {label}
      </span>
    </span>
  );
}

/** What the person will see, greyed, so the builder shows the shape of the answer. */
function Preview({ kind }: { kind: Kind }) {
  if (kind === 'rating')
    return (
      <div className="sv-preview" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((k) => (
          <Icon key={k} name="star" size={22} />
        ))}
      </div>
    );
  if (kind === 'nps')
    return (
      <div className="sv-preview sv-preview-nps" aria-hidden="true">
        {Array.from({ length: 11 }, (_, k) => (
          <span key={k}>{k}</span>
        ))}
      </div>
    );
  return (
    <div className="sv-preview" aria-hidden="true">
      <span className={`sv-preview-line${kind === 'long' ? ' tall' : ''}`}>{kind === 'date' ? t('sv.preview_date') : kind === 'long' ? t('sv.preview_long') : t('sv.preview_short')}</span>
    </div>
  );
}
